
import Vue, { PropType } from 'vue';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import {
	RunnitNodeField,
	IMAGE_UPLOAD_MODE,
	RunnitNodeRunInputValue,
	RunnitMask,
	RunnitNodeRunResult,
	RunnitUpload,
} from '@run-diffusion/shared';
import VideoUpload from '@/views/Runnits/RunnitSettings/ImageInput/VideoUpload.vue';
import RunnitSelectedVideo from '@/views/Runnits/RunnitSelectedVideo.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { db, storage } from '@/firebase';
// import InpaintingEditor from '@/views/Runnits/RunnitSettings/InpaintingEditor/InpaintingEditor.vue';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import _debounce from 'lodash/debounce';
import ComplexBackgroundBaseStyledMenu from '@/components/designElements/ComplexBackgroundBaseStyledMenu.vue';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'VideoInputField',
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
	},
	data () {
		return {
			IMAGE_UPLOAD_MODE,
			IMAGE_GALLERY_DIALOG_NAV,
			maskUrl: null,
			debouncedStrength: null,
			debouncedStrengthInput: null as ((value: number) => void) | null,
		};
	},
	created () {
		this.debouncedStrengthInput = _debounce((value: number): void => {
			this.onInput(this.videoValue,/* this.inpaintingValue, */ value);
			this.debouncedStrength = null;
		}, 100);
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
		if (this.debouncedStrengthInput) {
			this.debouncedStrengthInput.cancel();
		}
	},
	mixins: [
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	watch: {
		// inpaintingValue: {
		// 	immediate: true,
		// 	handler (newVal, oldVal) {
		// 		if (newVal !== oldVal) {
		// 			this.setMaskURL(this._get(newVal, 'mask.id') || null);
		// 		}
		// 	},
		// },
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				this.commitSingleSelectionStateValue(
					newVal,
					oldVal,
					this.field.uuid,
					this._get(this.videoValue, 'nodeRunResult.uuid') || null,
					this._get(this.videoValue, 'upload.id') || null,
					() => this.onInput(null, /* null, */ null),
					(value) => this.onInput(value, /* this.inpaintingValue, */ this.strengthValue),
				);
			},
		},
	},
	computed: {
		...mapState(['runnitState']),
		// ...mapGetters(['getInpaintingEditorState']),
		selectionStateTriggerId () {
			return `video-input-${this.field.uuid}`;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
		rightPercentage () {
			if (this.field.display.strengthWholeNumber) {
				// 0-100 range: value is already a percentage
				return Math.round(this.strengthValue || 0);
			} else {
				// 0-1 range: convert to percentage
				return Math.round((this.strengthValue || 0) * 100);
			}
		},
		leftPercentage () {
			// Always 100 - right percentage to ensure they add up to 100%
			return 100 - this.rightPercentage;
		},
		leftIcon () {
			return this.field.display.strengthImageWeight ? 'mdi-text' : 'mdi-video-outline';
		},
		rightIcon () {
			return this.field.display.strengthImageWeight ? 'mdi-video-outline' : 'mdi-text';
		},
		leftLabel () {
			return this.field.display.strengthImageWeight ? 'Prompt Weight' : 'Video Weight';
		},
		rightLabel () {
			return this.field.display.strengthImageWeight ? 'Video Weight' : 'Prompt Weight';
		},
		isVideoSelected () {
			return !!(
				this.videoValue &&
				(
					this.videoValue.nodeRunResult ||
					this.videoValue.upload
				)
			);
		},
		videoValue () {
			let val = this.value
			if (/** this.field.display.hasInpainting || */this.field.display.hasStrength) {
				val = this._get(this.value, 'video') || null;
			}
			return val;
		},
		// inpaintingValue () {
		// 	let val = null;
		// 	if (this.field.display.hasInpainting || this.field.display.hasStrength) {
		// 		val = this._get(this.value, 'inpainting') || null;
		// 	}
		// 	return val;
		// },
		strengthValue () {
			let val = null;
			if (/** this.field.display.hasInpainting || */this.field.display.hasStrength) {
				val = this._get(this.value, 'strength') || (this.field.display.strengthWholeNumber ? 80 : 0.8);
			}
			return val;
		},
		videoDuration () {
			// Get duration from either nodeRunResult or upload
			if (this.videoValue) {
				if (this._get(this.videoValue, 'nodeRunResult.file.duration')) {
					return this.videoValue.nodeRunResult.file.duration;
				}
				if (this._get(this.videoValue, 'upload.duration')) {
					return this.videoValue.upload.duration;
				}
			}
			return null;
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			// 'updateInpaintingEditorState',
			// 'removeInpaintingEditorState',
		]),
		openMenu () {
			this.menu = true;
		},
		triggerUpload (close: Function) {
			this.$refs.videoUpload.openFileDialog();
			close();
		},
		openLibrary (imageGalleryDialogInitialNav: ImageGalleryDialogNav, close: Function) {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['VID']);
			close();
		},
		clearVideo (close: Function) {
			this.onInput(null, /* this.inpaintingValue, */ null);
			close();
		},
		handleVideoUpload (upload: any) {
			if (upload.nodeRunResult) {
				this.onInput({ nodeRunResult: upload.nodeRunResult }, /* this.inpaintingValue, */ this.strengthValue);
			} else if (upload.upload) {
				this.onInput({ upload: upload.upload }, /* this.inpaintingValue, */ this.strengthValue);
			} else {
				this.onInput({ upload }, /* this.inpaintingValue, */ this.strengthValue);
			}
		},
		onInput (video: RunnitNodeRunInputValue, /* inpainting: RunnitNodeRunInputValue, */ strength: number) {
			if (/** this.field.display.hasInpainting || */this.field.display.hasStrength) {
				if (video || /** inpainting || */strength) {
					this.$emit('input', { video, /** inpainting, */ strength });
				} else {
					this.$emit('input', null);
				}
			} else {
				this.$emit('input', video);
			}
		},
		forwardDragEvent (method, event) {
			if (this.$refs.videoUpload && typeof this.$refs.videoUpload[method] === 'function') {
				this.$refs.videoUpload[method](event);
			}
		},
		// async handleInpaintingClick (close: Function) {
		// 	const currentState = this.getInpaintingEditorState(this.field.uuid);
		// 	if (currentState.saving) return;

		// 	const videoUrl = await this.fetchVideoUrl();

		// 	// Close other menus globally
		// 	this.updateRunnitState({
		// 		promptGeneratorOpen: false,
		// 		imageSelectOpen: false,
		// 	});

		// 	// Update field-specific state
		// 	if (currentState.open) {
		// 		this.removeInpaintingEditorState(this.field.uuid);
		// 	} else {
		// 		this.updateInpaintingEditorState({
		// 			fieldUuid: this.field.uuid,
		// 			editorState: {
		// 				open: true,
		// 				backgroundImageUrl: videoUrl,
		// 			},
		// 		});
		// 	}
		// 	close();
		// },
		async fetchVideoUrl () {
			if (this.videoValue.nodeRunResult) {
				const { imageUrl } = await this.fetchSrc(this.videoValue.nodeRunResult, 'nodeRunResult');
				return imageUrl;
			}
			if (this.videoValue.upload) {
				const { imageUrl } = await this.fetchSrc(this.videoValue.upload, 'upload');
				return imageUrl;
			}
			return null;
		},
		// onInpaintingMaskChange (mask) {
		// 	this.setMaskURL(this._get(mask, 'id') || null);
		// 	this.onInput(this.videoValue, { mask }, this.strengthValue);
		// },
		async setMaskURL (maskId) {
			if (maskId) {
				const maskRef = db.doc(`runnitMasks/${maskId}`);
				const mask: RunnitMask = (await maskRef.get()).data() as RunnitMask;

				const maskImageRef = storage.ref(`runnitMasks/users/${mask.userId}/masks/${mask.name}`);

				let maskURL = null;
				try {
					// Exists
					maskURL = await maskImageRef.getDownloadURL();
				} catch (err) {
					// doesn't exist
				}
				this.maskUrl = maskURL;
			} else {
				this.maskUrl = null;
			}
		},
		handleStrengthInput (value: number): void {
			this.debouncedStrength = value;
			if (this.debouncedStrengthInput) {
				this.debouncedStrengthInput(value);
			}
		},
	},
	components: {
		MenuOrBottomSheet,
		VideoUpload,
		RunnitSelectedVideo,
		// InpaintingEditor,
		ComplexBackgroundBaseStyledMenu,
	},
});
